import { LessonController, LessonPhases } from '../lesson';
import { QuizOverlay } from '../quiz/overlay';
import { showXpPopup, flashCard } from '../quiz/feedback';
import { getUiSoundPlayer } from '../ui-sounds';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP, skillUnlocked, recordQuestionAnswered } from '../../shared/progress';
import { findOrCreateKP, recordAttempt } from '../../shared/student-kp';
import { FACTIONS, factionForSubject } from '../../shared/factions';
import { SKILL_CATALOG } from '../../shared/skills';
import { mountEscapeButton } from '../escape-button';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { mountMascot } from '../mascot/mascot-ui';
import { checkAndUnlockBadges } from '../badges/badges';
import { isProMode } from '../settings';
import { computeCombatPower } from '../../game/progression/combat-power';
import { showCombatPowerGain } from '../combat-power-popup';
import type { Difficulty, GeneratedQuestion } from '../generators/math/types';
import type { Subject } from '../../shared/types';
import type { LearnDoneCallback } from '../app';

export type MathGenFn = (seed: number, difficulty: Difficulty) => GeneratedQuestion;

/**
 * Build a 10-MCQ + 5-fill style chapter purely from one generator.
 * Phases: 2 warmup (d1) + 3 guide (d1/d2) + 5 challenge (d2/d3) = 10 q.
 * The fill subset of "10 MCQ + 5 fill" is naturally produced by the generator
 * (e.g. division generators return `fill`, multiplication returns `mcq`).
 */
function buildSingleKpPhases(seedBase: number, gen: MathGenFn): LessonPhases {
  const make = (offset: number, d: Difficulty): GeneratedQuestion => gen(seedBase + offset * 7919, d);
  return {
    warmup: [make(0, 1), make(1, 1)],
    guide: [make(2, 1), make(3, 2), make(4, 2)],
    challenge: [make(5, 2), make(6, 3), make(7, 3), make(8, 3), make(9, 3)],
  };
}

interface RunOpts {
  subject: Subject;
  kpId: string;
  generator: MathGenFn;
  popupColor?: string;
}

/**
 * Internal driver shared by every math-KP lesson wrapper in this folder.
 * Mirrors src/learn/app.ts runMathLesson, but parameterised so multiple
 * single-KP lessons can plug in without duplicating ~100 lines each.
 */
export function runGenericMathKpLesson(
  container: HTMLElement,
  opts: RunOpts,
  onDone: LearnDoneCallback,
): void {
  const seed = Date.now() & 0xffff;
  let progress = loadProgress();
  if (!progress) progress = createFreshProgress('指揮官');

  // v24 教學專業模式 — boss directive 2026-05-22: 教學要專業.
  // 預設 ON ⇒ skip mascot, SFX, flashCard, XP popup. Toggle via 設定 → 📚 教學介面.
  const pro = isProMode(progress);

  const phases = buildSingleKpPhases(seed, opts.generator);
  const kp = findOrCreateKP(progress, opts.kpId, opts.subject);

  // Track scored-question accuracy for end-of-lesson SFX cue (task #31).
  // We only count scored answers (LessonController fires the callback for
  // every submitAnswer, regardless of phase) so warmup mistakes don't tank
  // the score and trigger a 「end of lesson」 sting on a kid who actually
  // did fine in challenge.
  let scoredTotal = 0;
  let scoredCorrect = 0;
  const lesson = new LessonController(phases, 2, (correct) => {
    // QA #B5 fix: warmup answers must NOT bump scoredTotal *or* the C06
    // dailyQCount bucket — warmup is "scratchpad" and should not count
    // toward the parent dashboard 答題數 column (was inflating daily count).
    const isWarmup = lesson.state === 'warmup';
    if (!isWarmup) {
      scoredTotal += 1;
      if (correct) scoredCorrect += 1;
    }
    recordAttempt(kp, correct ? 'correct' : 'wrong', new Date().toISOString());
    // C06 parent dashboard — bump today's per-day question count so the
    // 7d/30d summary's "答題數" column accumulates. Skip warmup so the
    // count reflects scored (guide+challenge) answers only.
    if (!isWarmup) {
      recordQuestionAnswered(progress!, 1);
    }
    saveProgress(progress!);
  });

  // C07 Mascot — 學習端陪伴角色 (legacy 遊戲化模式才掛). proMode ON → 不掛.
  // null sentinel + ?. so the rest of the wiring remains uniform.
  const mascot = pro ? null : mountMascot();

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    proMode: pro,
    onAnswer: (answer: number | string) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      const currentQ = lesson.currentQuestion();
      const currentPhase = lesson.state;
      const result = lesson.submitAnswer(answer);
      // proMode ON → 不做整卡綠/紅閃 (考試介面只用 option-label tick 標示).
      if (!pro && card) flashCard(card, result.correct ? 'correct' : 'wrong');
      // QA #B2 fix: 卷王速度 — drop mascot reaction delay 300→100ms so the
      // pet doesn't lag behind a fast-streaking kid (was minimum 800ms per
      // correct answer = pacing complaint).
      if (mascot) setTimeout(() => mascot.controller.react(result.combo, result.correct), 100);
      if (result.correct) {
        if (!pro) getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        if (!pro) getUiSoundPlayer().play('answer-wrong', 0.4);
        if (currentPhase !== 'warmup') {
          const correctAns = currentQ.kind === 'mcq'
            ? currentQ.options[currentQ.correctAnswer]
            : currentQ.kind === 'fill'
              ? currentQ.correctAnswer
              : '';
          recordMistake(progress!, {
            questionId: deriveQuestionId(currentQ.prompt),
            kp: currentQ.kp,
            subject: opts.subject,
            prompt: currentQ.prompt,
            correctAnswer: correctAns,
            userAnswer: answer,
          });
          saveProgress(progress!);
        }
      }
      // proMode ON → 不顯示「+N ⚡」浮動 popup,也不顯示「沒關係 +2」遊戲化文案.
      // XP 仍正常累積進 PlayerProgress(由 finish() 那段 addFactionXP 處理),
      // 答題結束的 summary card 會匯總顯示 — 答題執行畫面保持乾淨.
      if (!pro) {
        if (result.xp > 0) {
          const faction = FACTIONS[factionForSubject(opts.subject)];
          getUiSoundPlayer().play('xp-popup', 0.35);
          showXpPopup(`+${Math.round(result.xp)} ${faction.emoji}`, opts.popupColor ?? '#ffd54f');
        } else if (!result.correct) {
          showXpPopup('沒關係 +2', '#80cbc4');
        }
      }
      // QA #B2 fix: correct-answer next-question delay 500→300ms (卷王速度).
      // Wrong stays at 900ms to give the kid time to read the correct answer.
      setTimeout(() => {
        if (lesson.state === 'done') finish();
        else renderCurrent();
      }, result.correct ? 300 : 900);
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    mascot?.destroy();
    unmountEscape();
    onDone({ newUnlocks: [], exited: true });
  });

  function renderCurrent(): void {
    if (!overlay) return;
    overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
  }

  function finish(): void {
    unmountEscape();
    overlay?.destroy();
    overlay = null;
    mascot?.destroy();
    const faction = factionForSubject(opts.subject);
    const xp = Math.round(lesson.totalXP);
    // R-004 A3:入帳前先記戰力,入帳後若有上升 → 顯示「⚡戰力+N」浮動(無音效)。
    const powerBefore = computeCombatPower(progress!);
    addFactionXP(progress!, faction, xp);
    const newUnlocks: string[] = [];
    for (const skill of SKILL_CATALOG) {
      if (skill.faction === 'builtin') continue;
      if (skillUnlocked(progress!, skill.id)) continue;
      if (progress!.factionXP[skill.faction] >= skill.cost) {
        progress!.unlockedSkills.push(skill.id);
        progress!.skillLevels[skill.id] = Math.max(progress!.skillLevels[skill.id] ?? 0, 1);
        newUnlocks.push(skill.id);
      }
    }
    saveProgress(progress!);
    // 戰力上升(新解鎖技能會把 skillLevels 從 0→1)→ 克制的浮動提示。
    if (!pro) {
      const powerGain = computeCombatPower(progress!) - powerBefore;
      if (powerGain > 0) showCombatPowerGain(powerGain);
    }
    // C04 Badge — evaluate criteria after lesson ends (so kp-correct-count
    // and kp-mastered-count tier-ups fire immediately, not on next home visit).
    // Each unlock dispatches `ef:badge-unlocked` which mascot-ui already listens
    // for — but mascot is destroyed by this point, so the home banner picks up
    // `lastUnlocked` on the next render instead. checkAndUnlockBadges is
    // idempotent so calling it from both home + lesson-end is safe.
    try {
      checkAndUnlockBadges(progress!);
    } catch (err) {
      console.warn('[lesson] badge check failed (non-fatal):', err);
    }
    // End-of-lesson SFX (task #31). Three zones based on scored accuracy:
    //   ≥ 60% → fanfare (celebration)
    //   ≤ 30% → sting (encouragement to retry)
    //   middle → no end cue (per-question SFX already gave feedback)
    // proMode ON → silent (boss directive: 不需要有音樂).
    if (!pro && scoredTotal > 0) {
      const acc = scoredCorrect / scoredTotal;
      try {
        if (acc >= 0.6) getUiSoundPlayer().play('lesson-correct-fanfare', 0.55);
        else if (acc <= 0.3) getUiSoundPlayer().play('lesson-wrong-sting', 0.45);
      } catch { /* AudioContext busy / test harness */ }
    }
    onDone({ newUnlocks });
  }

  renderCurrent();
}
